import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ErrorModal.css';

/**
 * ErrorModal Component
 * User-friendly error display with retry options
 */
function ErrorModal({ 
  isOpen,
  onClose,
  error,
  onRetry,
  title = 'Something went wrong',
  showRetry = true,
  extraButton = null
}) {
  
  if (!isOpen) return null;

  // Parse error message
  const getErrorDetails = () => {
    if (!error) return { message: 'An unknown error occurred', solution: 'Please try again' };
    
    const errorStr = error.toString().toLowerCase();
    
    // Wallet connection errors
    if (errorStr.includes('wallet') || errorStr.includes('connect')) {
      return {
        icon: '👛',
        message: 'Wallet Connection Failed',
        solution: 'Please check that your wallet extension is installed and unlocked',
        action: 'Connect Wallet'
      };
    }
    
    // Transaction errors
    if (errorStr.includes('insufficient') || errorStr.includes('gas')) {
      return {
        icon: '⛽',
        message: 'Insufficient Gas',
        solution: 'You need more SUI to cover transaction fees',
        action: 'Add SUI to Wallet'
      };
    }
    
    // Network errors
    if (errorStr.includes('network') || errorStr.includes('timeout')) {
      return {
        icon: '🌐',
        message: 'Network Error',
        solution: 'Please check your internet connection and try again',
        action: 'Retry'
      };
    }
    
    // User rejected
    if (errorStr.includes('reject') || errorStr.includes('denied')) {
      return {
        icon: '🚫',
        message: 'Transaction Rejected',
        solution: 'You cancelled the transaction in your wallet',
        action: 'Try Again'
      };
    }
    
    // Generic error
    return {
      icon: '⚠️',
      message: 'Transaction Failed',
      solution: error.message || error.toString(),
      action: 'Try Again'
    };
  };

  const errorDetails = getErrorDetails();

  return (
    <AnimatePresence>
      <motion.div
        className="error-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="error-modal-content"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Icon */}
          <motion.div 
            className="error-icon"
            animate={{ 
              scale: [1, 1.1, 1],
            }}
            transition={{ 
              duration: 0.5,
              repeat: 2
            }}
          >
            {errorDetails.icon}
          </motion.div>

          {/* Title */}
          <h2 className="error-title">{title}</h2>

          {/* Message */}
          <div className="error-message">{errorDetails.message}</div>

          {/* Solution */}
          <div className="error-solution">
            <span className="solution-icon">💡</span>
            {errorDetails.solution}
          </div>

          {/* Buttons */}
          <div className="error-buttons">
            {showRetry && onRetry && (
              <motion.button
                className="error-btn error-btn-retry"
                onClick={() => {
                  onClose();
                  onRetry();
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {errorDetails.action}
              </motion.button>
            )}
            
            {extraButton && (
              <motion.button
                className="error-btn error-btn-quarantine"
                onClick={extraButton.onClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {extraButton.label}
              </motion.button>
            )}

            <motion.button
              className="error-btn error-btn-close"
              onClick={onClose}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Close
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ErrorModal;
